import axios from '@/plugins/axios';
import { PDFDocument, StandardFonts } from "pdf-lib";
import { makeSafeFilename } from '@/common/filename'
import { downloadBlob } from '@/common/fileDownload'

// ---- Public API -------------------------------------------------------------
async function getPublicationBibliography(publication, downloadType) {
    try {
        const { data } = await axios.get(
            `/api/publications/get_publication_bibliography_data/${publication.id}`
        );

        const type = String(downloadType || "pdf").toLowerCase();

        // Pick a consistent base name once
        const baseName = makeSafeFilename(
            data.title || `bibliography-${(publication && publication.sku)}`
        ) || "bibliography";

        if (type === "rtf") {
            const rtf = buildBibliographyRtf({ data, publication });
            const blob = new Blob([rtf], { type: "application/rtf;charset=utf-8" });
            downloadBlob(blob, `${baseName}.rtf`);
            return;
        }

        // Default to PDF
        const blob = await buildBibliographyPdfBlob({ data, publication });
        downloadBlob(blob, `${baseName}.pdf`);
    } catch (e) {
        console.error("getPublicationBibliography error:", e);
    }
}

// ---- PDF --------------------------------------------------------------------
async function buildBibliographyPdfBlob({ data, publication }) {
    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.TimesRoman);
    const fontBold = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);

    const pageSize = { width: 612, height: 792 }; // Letter
    const margin = 54;

    const titleSize = 18;
    const bodySize = 12;
    const lineHeight = bodySize * 1.35;

    const maxWidth = pageSize.width - margin * 2;

    let page = pdfDoc.addPage([pageSize.width, pageSize.height]);
    let y = pageSize.height - margin;

    const ensureSpace = (neededHeight) => {
        if (y - neededHeight < margin) {
            page = pdfDoc.addPage([pageSize.width, pageSize.height]);
            y = pageSize.height - margin;
        }
    };

    const titleLines = [
        "Bibliography",
        (publication && publication.sku) || "",
        (publication && publication.title) || "",
    ];

    titleLines.forEach((line) => {
        if (!line) return;

        const wrapped = wrapText(line, fontBold, titleSize, maxWidth);
        wrapped.forEach((wLine) => {
            ensureSpace(titleSize + 6);
            page.drawText(wLine, {
                x: margin,
                y: y - titleSize,
                size: titleSize,
                font: fontBold,
            });
            y -= titleSize + 6;
        });
    });

    y -= 16;
    y -= titleSize + 24;

    const entries = data && Array.isArray(data.citations) ? data.citations : [];

    for (let i = 0; i < entries.length; i++) {
        const entry = entries[i] || {};
        const location = entry.location || "";

        // Location line (bold)
        if (location) {
            const locLines = wrapText(location, fontBold, bodySize, maxWidth);
            for (let j = 0; j < locLines.length; j++) {
                ensureSpace(lineHeight);
                page.drawText(locLines[j], {
                    x: margin,
                    y: y - bodySize,
                    size: bodySize,
                    font: fontBold,
                });
                y -= lineHeight;
            }
        }

        // Citations: convert HTML -> plain text
        const citationHtml = entry.citations || "";
        const citationPlain = defaultHtmlToPlainText(citationHtml).trim();

        // Split into blocks
        const citationBlocks = citationPlain
            ? citationPlain
                .split(/\n+/)
                .map((s) => s.trim())
                .filter(Boolean)
            : ["(No citations)"];

        for (let k = 0; k < citationBlocks.length; k++) {
            const block = citationBlocks[k];
            const wrapped = wrapText(block, font, bodySize, maxWidth);

            for (let m = 0; m < wrapped.length; m++) {
                ensureSpace(lineHeight);
                page.drawText(wrapped[m], {
                    x: margin,
                    y: y - bodySize,
                    size: bodySize,
                    font: font,
                });
                y -= lineHeight;
            }

            // small spacing between citation blocks
            y -= 4;
        }

        // extra spacing between entries
        y -= 10;
    }

    const pdfBytes = await pdfDoc.save(); // Uint8Array from pdf-lib
    return new Blob([pdfBytes], { type: "application/pdf" });
}

// ---- RTF --------------------------------------------------------------------
function buildBibliographyRtf({ data, publication }) {
    const lines = [];
    lines.push({ text: "Bibliography", bold: true, fs: 36 });

    if (publication && publication.sku) {
        lines.push({ text: publication.sku, bold: true, fs: 28 });
    }

    if (publication && publication.title) {
        lines.push({ text: publication.title, bold: true, fs: 28 });
    }

    lines.push({ blank: true });

    const entries = data && Array.isArray(data.citations) ? data.citations : [];

    entries.forEach((entry) => {
        if (!entry) return;

        const location = entry.location || "";

        // Skip entries that have no citation content
        const raw = entry.citations;
        if (!raw || !String(raw).trim()) return;

        // Location line (bold)
        if (location) {
            lines.push({ text: location, bold: true, fs: 24 });
        }

        // Citations (HTML -> plain text)
        const citationText = defaultHtmlToPlainText(String(raw));

        const citationLines = String(citationText)
            .split("\n")
            .map((l) => l.trim())
            .filter(Boolean);

        if (citationLines.length) {
            citationLines.forEach((l) =>
                lines.push({ text: l, bold: false, fs: 24 })
            );
        } else {
            lines.push({ text: "", bold: false, fs: 24 });
        }

        lines.push({ blank: true });
    });

    return rtfFromLines(lines);
}

// ---- Helpers ----------------------------------------------------------------

function rtfFromLines(lines) {
    let rtf = "{\\rtf1\\ansi\\deff0";
    rtf += "{\\fonttbl{\\f0 Times New Roman;}}";
    rtf += "\\f0\\fs24 ";

    lines.forEach((line) => {
        if (line.blank) {
            rtf += "\\par\\par ";
            return;
        }

        const fs = line.fs || 24;

        if (line.bold) rtf += "\\b ";
        rtf += `\\fs${fs} ${rtfEscape(line.text || "")}`;
        if (line.bold) rtf += " \\b0";

        rtf += "\\par ";
    });

    rtf += "}";
    return rtf;
}

function rtfEscape(text) {
    let s = String(text)
        .replace(/\\/g, "\\\\")
        .replace(/{/g, "\\{")
        .replace(/}/g, "\\}");

    s = s.replace(/[^\x00-\x7F]/g, (ch) => `\\u${ch.charCodeAt(0)}?`);
    return s;
}

function defaultHtmlToPlainText(html) {
    if (!html) return "";

    const withBreaks = String(html)
        .replace(/<\s*br\s*\/?\s*>/gi, "\n")
        .replace(/<\/\s*p\s*>/gi, "\n")
        .replace(/<\s*p[^>]*>/gi, "");

    const stripped = withBreaks.replace(/<[^>]+>/g, "");

    const decoded = stripped
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">");

    return decoded
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .join("\n");
}

function wrapText(text, font, fontSize, maxWidth) {
    const words = String(text).split(/\s+/).filter(Boolean);
    const lines = [];
    let line = "";

    for (const word of words) {
        const testLine = line ? `${line} ${word}` : word;
        const width = font.widthOfTextAtSize(testLine, fontSize);

        if (width <= maxWidth) {
            line = testLine;
        } else {
            if (line) lines.push(line);

            if (font.widthOfTextAtSize(word, fontSize) > maxWidth) {
                const broken = breakLongWord(word, font, fontSize, maxWidth);
                lines.push(...broken.slice(0, -1));
                line = broken[broken.length - 1];
            } else {
                line = word;
            }
        }
    }

    if (line) lines.push(line);
    return lines;
}

function breakLongWord(word, font, fontSize, maxWidth) {
    const parts = [];
    let chunk = "";

    for (const ch of word) {
        const test = chunk + ch;

        if (font.widthOfTextAtSize(test, fontSize) <= maxWidth) {
            chunk = test;
        } else {
            if (chunk) parts.push(chunk);
            chunk = ch;
        }
    }

    if (chunk) parts.push(chunk);
    return parts;
}

export {
    getPublicationBibliography,
};
